import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowUpRight, CalendarDays, MapPin } from 'lucide-react';
import { API, resolveMedia, fmtEventDate } from '@/utils/media';

const Heading = ({ eyebrow, title, to, testid }) => (
  <div className="flex items-end justify-between gap-3 mb-5">
    <div className="min-w-0"><p className="text-[#FBBF24] text-[11px] tracking-[0.3em] uppercase mb-1">{eyebrow}</p><h2 className="font-heading text-2xl sm:text-3xl text-white leading-tight">{title}</h2></div>
    <Link to={to} className="shrink-0 inline-flex items-center gap-1 text-xs text-[#C4B5FD] hover:text-[#FBBF24]" data-testid={testid}>View all<ArrowUpRight size={14} /></Link>
  </div>
);

const Card = ({ to, img, title, meta, icon: Icon, i, testid }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.6 }} className="shrink-0 w-[78%] sm:w-auto snap-start">
    <Link to={to} className="group block relative h-64 rounded-3xl overflow-hidden border border-white/10 hover:border-[#F59E0B]/40" data-testid={testid}>
      <img src={img} alt={title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A14] via-[#0A0A14]/40 to-transparent" />
      <div className="absolute bottom-0 p-4">
        <p className="font-heading text-lg text-white leading-snug break-words">{title}</p>
        {meta && <p className="text-xs text-[#FDE68A] mt-1 flex items-center gap-1"><Icon size={12} />{meta}</p>}
      </div>
    </Link>
  </motion.div>
);

export default function ShowcaseStrip() {
  const [branches, setBranches] = useState([]);
  const [events, setEvents] = useState([]);
  useEffect(() => {
    axios.get(`${API}/public/branches`).then(r => setBranches(r.data.slice(0, 3))).catch(() => {});
    axios.get(`${API}/public/events?scope=upcoming`).then(r => setEvents(r.data.slice(0, 3))).catch(() => {});
  }, []);
  if (!branches.length && !events.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-5 py-16 sm:py-24 space-y-16" data-testid="showcase-strip">
      {events.length > 0 && (
        <div>
          <Heading eyebrow="Musicoul Live" title="Upcoming Events" to="/events" testid="showcase-events-link" />
          <div className="flex sm:grid sm:grid-cols-3 gap-4 overflow-x-auto no-scrollbar snap-x">
            {events.map((e, i) => <Card key={e.event_id} i={i} to={`/events/${e.event_id}`} img={resolveMedia(e.cover_image)} title={e.title} meta={fmtEventDate(e.start_at)} icon={CalendarDays} testid={`showcase-event-${e.event_id}`} />)}
          </div>
        </div>
      )}
      {branches.length > 0 && (
        <div>
          <Heading eyebrow="Our Centers" title="Visit a Branch" to="/branches" testid="showcase-branches-link" />
          <div className="flex sm:grid sm:grid-cols-3 gap-4 overflow-x-auto no-scrollbar snap-x">
            {branches.map((b, i) => <Card key={b.branch_id} i={i} to={`/branches/${b.branch_id}`} img={resolveMedia(b.cover_image) || 'https://images.unsplash.com/photo-1620942988625-74825774fdda?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200'} title={b.name} meta={b.location} icon={MapPin} testid={`showcase-branch-${b.branch_id}`} />)}
          </div>
        </div>
      )}
    </section>
  );
}
